import * as React from "react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import CssBaseline from "@mui/material/CssBaseline";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Stack from "@mui/material/Stack";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AppTheme from "../themes/AppTheme";
import AppAppBar from "../components/AppAppBar";
import Footer from "../components/Footer";
import { BacktestResultsGraph } from "../components/BacktestResultsGraph";
import { getSharedBacktest } from "../apis/junkyard";
import {
    GetSharedBacktestResponse,
    StorableBacktestResultRequest,
    TradingAlgoStored,
} from "../utils/typesTrading";
import { formatDateTime } from "../utils/datetime";

export const SharedBacktestPage: React.FC = () => {
    const navigate = useNavigate();
    const { uniqueId } = useParams<{ uniqueId: string }>();

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [backtest, setBacktest] = useState<StorableBacktestResultRequest | null>(null);
    const [tradingAlgo, setTradingAlgo] = useState<TradingAlgoStored | null>(null);
    const [sharedBy, setSharedBy] = useState<string | null>(null);
    const [expiresAt, setExpiresAt] = useState<string | null>(null);

    useEffect(() => {
        let cancelled = false;

        const loadShared = async () => {
            if (!uniqueId) {
                setError("No shared backtest identifier was provided.");
                setIsLoading(false);
                return;
            }

            try {
                setIsLoading(true);
                const resp: GetSharedBacktestResponse = await getSharedBacktest({
                    backtest_unique_id: uniqueId,
                });

                if (cancelled) return;

                if (resp.success && resp.backtest) {
                    setBacktest(resp.backtest);
                    setTradingAlgo(resp.trading_algo ?? null);
                    setSharedBy(resp.backtest.account_username ?? null);
                    setExpiresAt(resp.expires_at_utc ?? null);
                } else {
                    setError(resp.detail || "This shared backtest link is invalid or has expired.");
                }
            } catch (e) {
                console.error("Failed to load shared backtest", e);
                if (!cancelled) {
                    setError("An error occurred while loading the shared backtest.");
                }
            } finally {
                if (!cancelled) {
                    setIsLoading(false);
                }
            }
        };

        loadShared();

        return () => {
            cancelled = true;
        };
    }, [uniqueId]);

    const handleGoHome = () => {
        navigate("/");
    };

    const renderBody = () => {
        if (isLoading) {
            return (
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        mt: 16,
                    }}
                >
                    <Typography variant="h5" sx={{ mb: 4 }}>
                        Loading shared backtest…
                    </Typography>
                    <CircularProgress />
                </Box>
            );
        }

        if (error || !backtest) {
            return (
                <Box
                    sx={{
                        mt: 12,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 2,
                    }}
                >
                    <Alert severity="error" variant="filled" sx={{ maxWidth: 600 }}>
                        {error || "This shared backtest is unavailable."}
                    </Alert>
                    <Button variant="contained" onClick={handleGoHome}>
                        Go Home
                    </Button>
                </Box>
            );
        }

        return (
            <Box sx={{ marginTop: 10, width: "100%" }}>
                <Box sx={{ justifyContent: "center", display: "flex" }}>
                    <Typography variant="h3">Shared Backtest</Typography>
                </Box>
                {(sharedBy || expiresAt) && (
                    <Box sx={{ justifyContent: "center", display: "flex", mt: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            {/*{sharedBy ? `Shared by ${sharedBy}` : ""}*/}
                            {/*{sharedBy && expiresAt ? " · " : ""}*/}
                            {expiresAt ? `Link expires ${formatDateTime(expiresAt)}` : ""}
                        </Typography>
                    </Box>
                )}
                <Box
                    sx={{
                        justifyContent: "space-between",
                        display: "flex",
                        width: "100%",
                        borderBottom: 1,
                        borderColor: "divider",
                        pb: 1,
                        mb: 2,
                        mt: 2,
                    }}
                >
                    <Tooltip title="Go Home">
                        <IconButton onClick={handleGoHome} size="small">
                            <ArrowBackIcon />
                        </IconButton>
                    </Tooltip>
                    <Stack direction="row" spacing={1} />
                </Box>
                <Box sx={{ flexDirection: "row", height: "90%" }}>
                    <BacktestResultsGraph
                        selectedTradingAlgo={tradingAlgo}
                        backtestStartDate={new Date(backtest.backtest_start_date)}
                        backtestEndDate={new Date(backtest.backtest_end_date)}
                        backtestDates={backtest.backtest_dates}
                        stratPctReturns={backtest.strat_pct_returns}
                        bmarkPctReturns={backtest.bmark_pct_returns}
                        stratCashValues={backtest.strat_cash_values}
                        backtestResult={backtest.backtest_result}
                        onGoBack={handleGoHome}
                    />
                </Box>
            </Box>
        );
    };

    return (
        <AppTheme>
            <CssBaseline enableColorScheme />
            <AppAppBar />
            <Box
                sx={{
                    mt: 5,
                    display: "flex",
                    justifyContent: "center",
                    width: "99%",
                    minHeight: "80vh",
                    px: 2,
                }}
            >
                {renderBody()}
            </Box>
            <Footer />
        </AppTheme>
    );
};

export default SharedBacktestPage;

